/**
 * The costliest agent sessions this period (redesign phase 4d, catalogue A2).
 *
 * ── Why a leading indicator is worth a screen ───────────────────────────
 *
 * A monthly cap is a LAGGING control: it stops spend after the money is gone.
 * One runaway session — an agent loop, a mis-built mini-app, a 200-page
 * document pasted repeatedly — can burn a meaningful fraction of a tenant's
 * month in an afternoon, and nothing else in the product surfaces it until the
 * cap fires. It is also the first thing you want in front of you when a
 * customer disputes a bill.
 *
 * ── The endpoint was fixed before it got this page ──────────────────────
 *
 * As written it had no `WHERE` clause at all (every call grouped the entire
 * history of `agent_usage`), grouped by `model` so "top sessions" were really
 * top *(session, model)* pairs, and returned no `tenant_id` — so the first
 * question a platform operator asks, "whose session is this", was unanswerable
 * from the response. See the route's own note.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useRefreshWhenShown, useBillingCurrency } from '@papan/core-ui/ui'
import { formatMoney } from '@papan/types'
import { budgetApi, type ExpensiveSession } from '../../client/budget'

const LIMIT = 20

/**
 * How many rows render before the list is collapsed (F-9).
 *
 * The fetch still asks for `LIMIT` — the point of this panel is the TOP of a
 * cost distribution, and a shorter query would change which sessions are
 * candidates rather than just how many are drawn. What changes is how many are
 * SHOWN unasked.
 *
 * Twenty rows was roughly 1,000px, and on a live deployment they are
 * near-identical: the same tenant, the same model, costs a few cents apart. That
 * pushed Platform activity — the thing most worth reading on Operations — below
 * a wall of rows that all said the same thing. Five is enough to see whether
 * anything is an outlier, which is the question the panel exists to answer.
 */
const PREVIEW = 5

export function ExpensiveSessions({ onOpenCustomer }: {
  onOpenCustomer?: (tenantId: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const moneyOpts = { currency: useBillingCurrency() }
  const [sessions, setSessions] = useState<ExpensiveSession[] | null>(null)
  const [period, setPeriod] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await budgetApi.getExpensiveSessions({ limit: LIMIT })
      setSessions(r.sessions)
      setPeriod(r.period)
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // The shelf/settings deck stays MOUNTED behind `display: none`, so the mount
  // effect above is the only fetch this page would ever make — it would serve
  // whatever was true when the deck was first opened, with no error and no way
  // back but a full reload. See `DeckVisibility`.
  useRefreshWhenShown(() => { void load() })

  // `mt-8` as well as `mb-8`: the sibling above on Operations
  // (`StuckWebhooksPanel`) carries only a TOP margin, so a bottom-margin-only
  // section here butts its heading straight against that panel's last line.
  return (
    <div className="mt-8 mb-8">
      <div className="flex items-center justify-between gap-4 mb-1">
        <h2 className="themed-text text-lg font-medium">
          {t('settings.platform.sessions.title', { defaultValue: 'Expensive sessions' })}
        </h2>
        <button
          onClick={() => void load()}
          disabled={loading}
          className="text-xs themed-text-muted hover:themed-text transition-colors disabled:opacity-60 disabled:cursor-default"
        >
          {/* No icon: `@papan/orgs` does not depend on lucide-react, and a
              text-only control needs no glyph to be understood. */}
          {t('settings.platform.sessions.refresh', { defaultValue: 'Refresh' })}
        </button>
      </div>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.platform.sessions.desc', {
          defaultValue:
            'The costliest agent sessions this period, across every tenant. A single runaway session can burn a month of budget in an afternoon, which a monthly cap only notices afterwards.',
        })}
        {period && <span className="themed-text-muted"> ({period})</span>}
      </p>

      {error && <div className="text-sm text-red-400">{error}</div>}

      {sessions && sessions.length === 0 && (
        <div className="themed-text-muted text-sm">{t('settings.budget.noData')}</div>
      )}

      {sessions && sessions.length > 0 && (
        <div
          className={`themed-bg-secondary rounded-lg overflow-hidden transition-opacity ${loading ? 'opacity-60' : ''}`}
          data-testid="expensive-sessions"
          aria-busy={loading}
        >
          {(showAll ? sessions : sessions.slice(0, PREVIEW)).map((s) => (
            <div key={s.sessionId} className="px-3 py-2.5 border-b themed-border last:border-b-0 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {/* Tenant FIRST: on a platform console the owning customer is
                      the identifying fact, not the session id. */}
                  {onOpenCustomer ? (
                    <button
                      onClick={() => onOpenCustomer(s.tenantId)}
                      className="themed-text text-sm hover:underline truncate max-w-[220px]"
                    >
                      {s.tenantName ?? s.tenantId}
                    </button>
                  ) : (
                    <span className="themed-text text-sm truncate max-w-[220px]">{s.tenantName ?? s.tenantId}</span>
                  )}
                  {/* Funded by ANOTHER wallet (0180). The row is named and
                      linked by the WORKSPACE — where the session ran, which is
                      what an operator chasing a runaway loop needs — so the
                      wallet has to be said out loud, or the cost reads as the
                      customer's. Present only when the two differ.

                      Deliberately does not say "the platform": the payer axis
                      also carries an app's owner-pays contract, so naming the
                      platform here would be false the day `app-payer` goes
                      live. The id is shown instead, which is true whatever
                      funded it. */}
                  {s.payerTenantId && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400 whitespace-nowrap"
                      title={t('settings.platform.sessions.payerHint', {
                        defaultValue:
                          'This session ran in the workspace named beside it, and its cost was billed to {{payer}} rather than to that tenant.',
                        payer: s.payerTenantId,
                      })}
                    >
                      {t('settings.platform.sessions.payer', { defaultValue: 'Funded elsewhere' })}
                    </span>
                  )}
                  {s.email && <span className="themed-text-muted text-xs truncate">{s.email}</span>}
                </div>
                <div className="text-[11px] themed-text-muted font-mono mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="truncate max-w-[200px]" title={s.sessionId}>{s.sessionId}</span>
                  <span>·</span>
                  {/* Every model the session used. A session that switched
                      models mid-run is exactly the shape of an agent loop, so
                      this is signal rather than trivia — and it used to be a
                      row-multiplier that made the list lie. */}
                  <span className="truncate max-w-[240px]">{s.models.join(', ')}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="themed-text text-sm tabular-nums">{formatMoney(s.costBillingMicro, moneyOpts)}</div>
                <div className="text-[11px] themed-text-muted tabular-nums">
                  {t('settings.platform.sessions.turns', {
                    defaultValue: '{{count}} turn · {{min}} min',
                    defaultValue_other: '{{count}} turns · {{min}} min',
                    count: s.messages,
                    min: s.durationMin,
                  })}
                </div>
              </div>
            </div>
          ))}

          {/* Only when there is something hidden. A permanently-present toggle
              on a list of three would claim there is more to see. */}
          {sessions.length > PREVIEW && (
            <button
              onClick={() => setShowAll(v => !v)}
              className="w-full px-3 py-2 text-xs themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors text-left"
            >
              {showAll
                ? t('settings.platform.sessions.showFewer', { defaultValue: 'Show fewer' })
                : t('settings.platform.sessions.showAll', {
                    defaultValue: 'Show all {{count}} session',
                    defaultValue_other: 'Show all {{count}} sessions',
                    count: sessions.length,
                  })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
